import { Cpu, HardDrive, MemoryStick, Network, Shield, Users, Activity, Clock, AlertTriangle } from "lucide-react";
import React from "react";

export interface StatusIssue {
  metric: string;
  value: string | number;
  threshold: string;
  severity: 'warning' | 'critical';
  icon: React.ReactNode;
  action: string;
  category: string;
}

export interface ServerStatusResult {
  issues: StatusIssue[];
  overallStatus: 'online' | 'warning' | 'critical';
}

// Helper to create icon elements
const createIcon = (Icon: any, className: string = "h-3 w-3") => 
  React.createElement(Icon, { className });

export function getServerStatusIssues(server: any): StatusIssue[] {
  const issues: StatusIssue[] = [];

  // ============ MEMORY ============
  // Memory commit (Windows) - memory_committed_percent
  const memCommit = server.memory_committed_percent;
  if (memCommit !== null && memCommit !== undefined) {
    if (memCommit > 90) {
      issues.push({
        metric: 'Memory Commit',
        value: `${memCommit.toFixed(1)}%`,
        threshold: '> 90%',
        severity: 'critical',
        icon: createIcon(MemoryStick),
        action: 'Free up memory or add more RAM to prevent OOM/hard paging',
        category: 'Memory'
      });
    } else if (memCommit > 80) {
      issues.push({
        metric: 'Memory Commit',
        value: `${memCommit.toFixed(1)}%`,
        threshold: '> 80%',
        severity: 'warning',
        icon: createIcon(MemoryStick),
        action: 'Monitor memory usage, consider optimizing applications',
        category: 'Memory'
      });
    }
  }

  // ============ DISK SPACE ============
  // Check disk_volumes for free space (GB based)
  // disk_volumes can be a single object OR an array
  const diskVolumes = server.disk_volumes;
  const volumesArray = diskVolumes 
    ? (Array.isArray(diskVolumes) ? diskVolumes : [diskVolumes])
    : [];
  
  for (const vol of volumesArray) {
    const driveLetterRaw = vol?.drive_letter ?? vol?.driveLetter ?? vol?.letter;
    // Ignore hidden partitions / mount points without a letter to avoid false "Disk Space (Unknown)" alerts
    if (!driveLetterRaw || typeof driveLetterRaw !== 'string') continue;

    const driveLetter = driveLetterRaw.replace(':', '').trim().toUpperCase();
    if (!/^[A-Z]$/.test(driveLetter)) continue;

    const freeGb = vol.free_gb;
    const isSystemDrive = driveLetter === 'C';

    if (freeGb !== null && freeGb !== undefined) {
        if (isSystemDrive) {
          // System/OS volume (C:) - stricter thresholds
          if (freeGb < 10) {
            issues.push({
              metric: `Disk Space (${driveLetter}:)`,
              value: `${freeGb.toFixed(1)} GB free`,
              threshold: '< 10 GB (system drive)',
              severity: 'critical',
              icon: createIcon(HardDrive),
              action: 'Clean up system drive immediately - Windows updates, temp files, logs at risk',
              category: 'Disk'
            });
          } else if (freeGb < 25) {
            issues.push({
              metric: `Disk Space (${driveLetter}:)`,
              value: `${freeGb.toFixed(1)} GB free`,
              threshold: '< 25 GB (system drive)',
              severity: 'warning',
              icon: createIcon(HardDrive),
              action: 'Clean up system drive - remove temp files, old logs',
              category: 'Disk'
            });
          }
        } else {
          // Non-system volumes
          if (freeGb < 20) {
            issues.push({
              metric: `Disk Space (${driveLetter}:)`,
              value: `${freeGb.toFixed(1)} GB free`,
              threshold: '< 20 GB',
              severity: 'critical',
              icon: createIcon(HardDrive),
              action: 'Clean up disk space or expand storage capacity',
              category: 'Disk'
            });
          } else if (freeGb < 50) {
            issues.push({
              metric: `Disk Space (${driveLetter}:)`,
              value: `${freeGb.toFixed(1)} GB free`,
              threshold: '< 50 GB',
              severity: 'warning',
              icon: createIcon(HardDrive),
              action: 'Review disk usage and remove unnecessary files',
              category: 'Disk'
            });
          }
        }
    }
  }

  // ============ DISK PERFORMANCE ============
  // Read/Write latency
  const readLatency = server.disk_read_latency_ms;
  const writeLatency = server.disk_write_latency_ms;
  const maxLatency = Math.max(readLatency || 0, writeLatency || 0);

  if (maxLatency > 0) {
    if (maxLatency > 50) {
      issues.push({
        metric: 'Disk Latency',
        value: `${maxLatency.toFixed(1)} ms`,
        threshold: '> 50 ms',
        severity: 'critical',
        icon: createIcon(HardDrive),
        action: 'Check disk health, consider SSD upgrade or reduce I/O load',
        category: 'Disk'
      });
    } else if (maxLatency > 20) {
      issues.push({
        metric: 'Disk Latency',
        value: `${maxLatency.toFixed(1)} ms`,
        threshold: '> 20 ms',
        severity: 'warning',
        icon: createIcon(HardDrive),
        action: 'Monitor disk performance, check for I/O bottlenecks',
        category: 'Disk'
      });
    }
  }

  // ============ NETWORK ============
  // Packet errors (per minute approximation)
  const netErrors = server.network_errors;
  if (netErrors !== null && netErrors !== undefined) {
    if (netErrors > 100) {
      issues.push({
        metric: 'Network Errors',
        value: `${netErrors}/min`,
        threshold: '> 100/min',
        severity: 'critical',
        icon: createIcon(Network),
        action: 'Check network cables, NIC, and switch ports',
        category: 'Network'
      });
    } else if (netErrors > 10) {
      issues.push({
        metric: 'Network Errors',
        value: `${netErrors}/min`,
        threshold: '> 10/min',
        severity: 'warning',
        icon: createIcon(Network),
        action: 'Monitor network quality, check for interference',
        category: 'Network'
      });
    }
  }

  // TCP connections
  const tcpConnections = server.tcp_established_connections;
  if (tcpConnections !== null && tcpConnections !== undefined) {
    if (tcpConnections > 5000) {
      issues.push({
        metric: 'TCP Connections',
        value: tcpConnections,
        threshold: '> 5,000',
        severity: 'critical',
        icon: createIcon(Network),
        action: 'Check for connection floods or leaks, review application behavior',
        category: 'Network'
      });
    } else if (tcpConnections > 1000) {
      issues.push({
        metric: 'TCP Connections',
        value: tcpConnections,
        threshold: '> 1,000',
        severity: 'warning',
        icon: createIcon(Network),
        action: 'Monitor connection patterns, check for potential issues',
        category: 'Network'
      });
    }
  }

  // ============ SECURITY ============
  // Windows Defender
  const defenderEnabled = server.windows_defender_enabled;
  const defenderUpdated = server.windows_defender_updated;

  if (defenderEnabled === false) {
    issues.push({
      metric: 'Windows Defender',
      value: 'Inactive',
      threshold: 'Must be active',
      severity: 'critical',
      icon: createIcon(Shield),
      action: 'Enable Windows Defender immediately',
      category: 'Security'
    });
  } else if (defenderUpdated === false) {
    issues.push({
      metric: 'Windows Defender',
      value: 'Signatures outdated',
      threshold: '> 3 days old',
      severity: 'warning',
      icon: createIcon(Shield),
      action: 'Update Windows Defender signatures',
      category: 'Security'
    });
  }

  // Firewall - Critical only (no warning)
  const firewallEnabled = server.firewall_enabled;
  if (firewallEnabled === false) {
    issues.push({
      metric: 'Firewall',
      value: 'Disabled',
      threshold: 'Must be enabled',
      severity: 'critical',
      icon: createIcon(Shield),
      action: 'Enable Windows Firewall immediately',
      category: 'Security'
    });
  }

  // BitLocker
  const bitlockerStatus = server.bitlocker_status;
  if (bitlockerStatus && Array.isArray(bitlockerStatus)) {
    const osVolume = bitlockerStatus.find((v: any) => v.mount_point === 'C:');
    if (osVolume && osVolume.protection_status !== 'On') {
      issues.push({
        metric: 'BitLocker (C:)',
        value: osVolume.protection_status || 'Not encrypted',
        threshold: 'OS volume must be encrypted',
        severity: 'critical',
        icon: createIcon(Shield),
        action: 'Enable BitLocker encryption on the OS volume',
        category: 'Security'
      });
    }
  }

  // Secure Boot
  const secureBoot = server.secure_boot_enabled;
  if (secureBoot === false) {
    issues.push({
      metric: 'Secure Boot',
      value: 'Disabled',
      threshold: 'Should be enabled',
      severity: 'warning',
      icon: createIcon(Shield),
      action: 'Enable Secure Boot in BIOS/UEFI settings',
      category: 'Security'
    });
  } else if (secureBoot === null || secureBoot === undefined) {
    issues.push({
      metric: 'Secure Boot',
      value: 'Unknown',
      threshold: 'Status unknown',
      severity: 'warning',
      icon: createIcon(Shield),
      action: 'Verify Secure Boot status',
      category: 'Security'
    });
  }

  // TPM
  const tpmEnabled = server.tpm_enabled;
  if (tpmEnabled === false) {
    issues.push({
      metric: 'TPM',
      value: 'Disabled',
      threshold: 'Should be enabled',
      severity: 'warning',
      icon: createIcon(Shield),
      action: 'Enable TPM in BIOS/UEFI settings',
      category: 'Security'
    });
  } else if (tpmEnabled === null || tpmEnabled === undefined) {
    issues.push({
      metric: 'TPM',
      value: 'Unknown',
      threshold: 'Status unknown',
      severity: 'warning',
      icon: createIcon(Shield),
      action: 'Verify TPM status',
      category: 'Security'
    });
  }

  // ============ FAILED LOGINS ============
  const failedLogins = server.failed_login_last_24h;
  if (failedLogins !== null && failedLogins !== undefined) {
    if (failedLogins > 50) {
      issues.push({
        metric: 'Failed Logins (24h)',
        value: failedLogins,
        threshold: '> 50',
        severity: 'critical',
        icon: createIcon(Users),
        action: 'Investigate potential brute force attack, review security logs',
        category: 'Security'
      });
    } else if (failedLogins > 10) {
      issues.push({
        metric: 'Failed Logins (24h)',
        value: failedLogins,
        threshold: '> 10',
        severity: 'warning',
        icon: createIcon(Users),
        action: 'Review failed login attempts in security logs',
        category: 'Security'
      });
    }
  }

  // ============ PRIVILEGED ACCOUNTS ============
  const adminCount = server.admin_accounts_count;
  if (adminCount !== null && adminCount !== undefined) {
    if (adminCount > 10) {
      issues.push({
        metric: 'Admin Accounts',
        value: adminCount,
        threshold: '> 10',
        severity: 'critical',
        icon: createIcon(Users),
        action: 'Review and reduce number of admin accounts',
        category: 'Security'
      });
    } else if (adminCount > 5) {
      issues.push({
        metric: 'Admin Accounts',
        value: adminCount,
        threshold: '> 5',
        severity: 'warning',
        icon: createIcon(Users),
        action: 'Consider reducing admin account count',
        category: 'Security'
      });
    }
  }

  // ============ TOP PROCESSES ============
  // Process CPU checks removed per user request

  const topMem = server.top_processes_memory;
  if (topMem && Array.isArray(topMem)) {
    for (const proc of topMem) {
      const memMb = proc.mem_mb;
      if (memMb !== null && memMb !== undefined) {
        const memGb = memMb / 1024;
        if (memGb > 8) {
          issues.push({
            metric: `Process Memory (${proc.name})`,
            value: `${memGb.toFixed(1)} GB`,
            threshold: '> 8 GB',
            severity: 'critical',
            icon: createIcon(MemoryStick),
            action: `Investigate high memory usage by ${proc.name}`,
            category: 'Processes'
          });
        } else if (memGb > 4) {
          issues.push({
            metric: `Process Memory (${proc.name})`,
            value: `${memGb.toFixed(1)} GB`,
            threshold: '> 4 GB',
            severity: 'warning',
            icon: createIcon(MemoryStick),
            action: `Monitor ${proc.name} memory usage`,
            category: 'Processes'
          });
        }
      }
    }
  }

  return issues;
}

export function getOverallStatus(issues: StatusIssue[]): 'online' | 'warning' | 'critical' {
  if (issues.some(i => i.severity === 'critical')) return 'critical';
  if (issues.some(i => i.severity === 'warning')) return 'warning';
  return 'online';
}

// Simplified version for table display
export function getSystemStatusSummary(server: any): { metric: string; severity: 'warning' | 'critical'; value: string | number }[] {
  const issues = getServerStatusIssues(server);
  return issues.map(i => ({ metric: i.metric, severity: i.severity, value: i.value }));
}
